import { Tooltip } from '@neo4j-ndl/react';
import { InformationCircleIconOutline } from '@neo4j-ndl/react/icons';
import type { FleetManagementTypes } from '@nx/state';
import { isNullish } from '@nx/stdlib';
import { DataGridHelpers } from '@nx/ui';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';

import { columnPreferencesColumn } from '../../../shared/components/column-preferences';
import { NumberCell, TextCell, TimestampCell } from '../../../shared/helpers/table-overrides';
import { DeploymentStatus } from '../../shared/components/deployment-status';
import { PluginDownloadLinks } from '../../shared/components/plugin-download-links';
import { hasOutdatedPlugin } from '../../shared/components/server-status';
import { LATEST_PLUGIN_VERSION } from '../../shared/constants';
import type { TabKey } from '../types';

const columnHelperServer = createColumnHelper<FleetManagementTypes.Server>();
const columnHelperDatabase = createColumnHelper<FleetManagementTypes.Database>();
const columnHelperDatabaseInstance = createColumnHelper<FleetManagementTypes.DatabaseInstance>();

const PluginVersionCell = ({ version }: { version: string }) => {
  const hasPluginUpdate = useMemo(() => hasOutdatedPlugin(version), [version]);
  return (
    <>
      {hasPluginUpdate && (
        <Tooltip type="rich">
          <Tooltip.Trigger>
            <InformationCircleIconOutline className="mr-1 size-4" />
          </Tooltip.Trigger>
          <Tooltip.Content>
            Consider updating the Fleet management plugin to the latest version {LATEST_PLUGIN_VERSION}. Download the
            plugin matching your Neo4j version from the link below.
            <PluginDownloadLinks />
          </Tooltip.Content>
        </Tooltip>
      )}
      <DataGridHelpers.TruncatedColumn value={version} />
    </>
  );
};

const serverColumns = [
  columnHelperServer.accessor('name', {
    header: 'Name',
    size: 335,
    cell: TextCell,
  }),
  columnHelperServer.accessor('address', {
    header: 'Address',
    size: 200,
    cell: TextCell,
  }),
  columnHelperServer.accessor('serverId', {
    header: 'Server ID',
    size: 335,
    cell: TextCell,
  }),
  columnHelperServer.accessor('status', {
    header: 'Status',
    size: 150,
    cell: (cx) => <DeploymentStatus status={cx.getValue()} />,
  }),
  columnHelperServer.accessor('health', {
    header: 'Health',
    size: 150,
    cell: TextCell,
  }),
  columnHelperServer.accessor('state', {
    header: 'State',
    size: 150,
    cell: TextCell,
  }),
  columnHelperServer.accessor('modeConstraint', {
    header: 'Mode Constraint',
    size: 190,
    cell: TextCell,
  }),
  columnHelperServer.accessor('version', {
    header: 'Version',
    size: 150,
    cell: TextCell,
  }),
  columnHelperServer.accessor('pluginVersion', {
    header: 'Plugin Version',
    size: 150,
    cell: ({ row }) => {
      return <PluginVersionCell version={row.original.pluginVersion} />;
    },
    meta: {
      fleetManagement: {
        minor: true,
      },
    },
  }),
  columnHelperServer.accessor('lastPing', {
    header: 'Last Ping',
    size: 150,
    cell: TimestampCell,
  }),
];

const databaseColumns = [
  columnHelperDatabase.accessor('name', {
    header: 'Name',
    size: 190,
    cell: TextCell,
  }),
  columnHelperDatabase.accessor('databaseId', {
    header: 'Database Id',
    size: 620,
    cell: TextCell,
  }),
];

const databaseInstanceColumns = [
  columnHelperDatabaseInstance.accessor('serverName', {
    header: 'Server Name',
    size: 335,
    cell: TextCell,
  }),
  columnHelperDatabaseInstance.accessor('serverId', {
    header: 'Server Id',
    size: 335,
    cell: TextCell,
  }),
  columnHelperDatabaseInstance.accessor('databaseName', {
    header: 'Database Name',
    size: 200,
    cell: TextCell,
  }),
  columnHelperDatabaseInstance.accessor('access', {
    header: 'Access',
    size: 120,
    cell: TextCell,
  }),
  columnHelperDatabaseInstance.accessor('aliases', {
    header: 'Aliases',
    size: 130,
  }),
  columnHelperDatabaseInstance.accessor('role', {
    header: 'Role',
    size: 130,
    cell: TextCell,
  }),
  columnHelperDatabaseInstance.accessor('requestedStatus', {
    header: 'Requested Status',
    size: 175,
    cell: TextCell,
  }),
  columnHelperDatabaseInstance.accessor('currentStatus', {
    header: 'Current Status',
    size: 175,
    cell: TextCell,
  }),
  columnHelperDatabaseInstance.accessor('statusMessage', {
    header: 'Status Message',
    size: 190,
    cell: TextCell,
  }),
  columnHelperDatabaseInstance.accessor('default', {
    header: 'Default',
    size: 110,
  }),
  columnHelperDatabaseInstance.accessor('currentPrimariesCount', {
    header: 'Current Primaries Count',
    size: 250,
    cell: NumberCell,
  }),
  columnHelperDatabaseInstance.accessor('currentSecondariesCount', {
    header: 'Current Secondaries Count',
    size: 250,
    cell: NumberCell,
  }),
  columnHelperDatabaseInstance.accessor('requestedPrimariesCount', {
    header: 'Requested Primaries Count',
    size: 250,
    cell: NumberCell,
  }),
  columnHelperDatabaseInstance.accessor('requestedSecondariesCount', {
    header: 'Requested Secondaries Count',
    size: 250,
    cell: NumberCell,
  }),
  columnHelperDatabaseInstance.accessor('creationTime', {
    header: 'Creation Time',
    size: 150,
    cell: TimestampCell,
  }),
  columnHelperDatabaseInstance.accessor('lastStartTime', {
    header: 'Last Start Time',
    size: 150,
    cell: TimestampCell,
  }),
  columnHelperDatabaseInstance.accessor('store', {
    header: 'Store Format',
    size: 175,
    cell: TextCell,
  }),
  columnHelperDatabaseInstance.accessor('writer', {
    header: 'Writer',
    size: 110,
  }),
  columnHelperDatabaseInstance.accessor('lastCommittedTxn', {
    header: 'Last Committed Transaction',
    size: 250,
    cell: ({ row }) => {
      if (isNullish(row.original.lastCommittedTxn)) {
        return <DataGridHelpers.TruncatedColumn value={'N/A'} />;
      }
      return NumberCell;
    },
  }),
  columnHelperDatabaseInstance.accessor('replicationLag', {
    header: 'Replication Lag',
    size: 250,
    cell: NumberCell,
  }),
];

export const useColumns = <T extends TabKey>(table: T, hasColumnPreferences = true, includeMinor = true) => {
  const columns = useMemo(() => {
    if (table === 'SERVERS') {
      if (hasColumnPreferences) {
        return [
          ...serverColumns,
          columnHelperServer.display({
            ...columnPreferencesColumn,
          }),
        ];
      }
      return serverColumns;
    }

    if (table === 'DATABASES') {
      if (hasColumnPreferences) {
        return [
          ...databaseColumns,
          columnHelperDatabase.display({
            ...columnPreferencesColumn,
          }),
        ];
      }
      return databaseColumns;
    }

    if (table === 'DATABASE_INSTANCES') {
      if (hasColumnPreferences) {
        return [
          ...databaseInstanceColumns,
          columnHelperDatabaseInstance.display({
            ...columnPreferencesColumn,
          }),
        ];
      }
      return databaseInstanceColumns;
    }

    return [];
  }, [table, hasColumnPreferences]);

  return columns.filter((column) => {
    return includeMinor || !(column.meta?.fleetManagement?.minor ?? false);
  });
};
